import React, { useRef, useState, useEffect } from "react";
import { observer } from "mobx-react";
import { useHistory } from "react-router-dom";
import WidgetCard from "../Widgets/WidgetCard";
import { handleViewRecordsClick, isViewRecordsAvailable } from "../utils/viewRecordsNavigation";
import CountWidget from "../Widgets/CountWidget";
import RateWidget from "../Widgets/RateWidget";
import CallOutcomesWidget from "../Widgets/CallOutcomesWidget";
import LinkedInOverviewWidget from "../Widgets/LinkedInOverviewWidget";
import CallsByMemberWidget from "../Widgets/CallsByMemberWidget";
import LatestRepliesWidget from "../Widgets/LatestRepliesWidget";
import WidgetCardSkeleton from "../Widgets/WidgetCardSkeleton";
import ContactStagesWidget from "../Widgets/ContactStagesWidget";
import ContactsAddedWidget from "../Widgets/ContactsAddedWidget";
import CallsUtils from "Utils/CallsUtils";
import { WIDGET_META, RATE_WIDGET_IDS, widgetHasGraph, widgetHasMemberBreakdown, getDefaultVisualization } from "../utils/widgetConstants";
import widgetDataStore from "../store/widgetDataStore";
import dashboardV2Store from "../store/dashboardV2Store";
import { toasterState } from "Components/common/toaster";
import { ReactComponent as PinIcon } from "Assets/svg/pin.svg";
import userDetail from "Stores/userDetail";

const DATE_PRESET_LABELS = {
  today: "Today",
  yesterday: "Yesterday",
  lastWeek: "Last week",
  "7d": "Last 7 days",
  "30d": "Last 30 days",
  thisMonth: "This month"
};

const renderWidgetContent = (w, data, isLoading, breakdownEnabled = false, isSwitchingVisualization = false, isBreakdownLoading = false) => {
  const meta = WIDGET_META[w.widgetId];
  const isRate = RATE_WIDGET_IDS.has(w.widgetId);
  // SG-2164: "<valueKey>ByMember" is the convention DashboardService uses for KPI/trend widgets;
  // Contact Stages/Contacts Added use their own explicit keys (contactStagesByMember/contactsAddedByMember).
  const memberBreakdown = breakdownEnabled && meta?.valueKey ? data?.[`${meta.valueKey}ByMember`] : null;
  // SG-2164: Visualization Type (Line/Bar/KPI) — only meaningful for widgets that have a series at
  // all (widgetHasGraph). Undefined/null here preserves today's default rendering unchanged.
  const visualization = widgetHasGraph(w.widgetId) ? w.visualization : null;

  if (isSwitchingVisualization) {
    return <WidgetCardSkeleton />;
  }

  if (isRate) {
    return (
      <RateWidget
        currentRate={data?.currentRate}
        trend={data?.trend}
        chartData={data?.chartData || []}
        dateFilter={widgetDataStore.activeDateFilter}
        metricLabel={meta?.label}
        visualization={visualization}
      />
    );
  }
  if (w.widgetId === "CALL_OUTCOMES") {
    return <CallOutcomesWidget callOutcomes={data?.callOutcomes || []} visualization={visualization} />;
  }
  if (w.widgetId === "LINKEDIN_OVERVIEW") {
    return <LinkedInOverviewWidget data={data || {}} />;
  }
  if (w.widgetId === "CALLS_BY_MEMBER") {
    return <CallsByMemberWidget callsByMember={data?.callsByMember || []} />;
  }
  if (w.widgetId === "LATEST_REPLIES") {
    return <LatestRepliesWidget replies={data?.replies || []} isLoading={isLoading} />;
  }
  if (w.widgetId === "AVG_CALL_DURATION") {
    const seconds = data?.avgCallDuration;
    return (
      <CountWidget
        value={seconds != null ? CallsUtils.secondsToTime(seconds) : null}
        unit={seconds != null ? (seconds >= 3600 ? "hours" : "mins") : null}
        trend={data?.avgCallDurationTrend}
        series={null}
        dateFilter={widgetDataStore.activeDateFilter}
        customRange={widgetDataStore.activeCustomRange}
        memberBreakdown={memberBreakdown}
        breakdownLoading={isBreakdownLoading}
      />
    );
  }
  // SG-2164: new widgets
  if (w.widgetId === "CONTACT_STAGES") {
    return (
      <ContactStagesWidget
        contactStages={data?.contactStages || []}
        memberBreakdown={breakdownEnabled ? data?.contactStagesByMember : null}
        breakdownLoading={isBreakdownLoading}
        visualization={visualization}
      />
    );
  }
  if (w.widgetId === "CONTACTS_ADDED") {
    return (
      <ContactsAddedWidget
        total={data?.contactsAddedTotal}
        trend={data?.contactsAddedTotalTrend}
        contactsAddedBreakdown={data?.contactsAddedBreakdown || []}
        memberBreakdown={breakdownEnabled ? data?.contactsAddedByMember : null}
        breakdownLoading={isBreakdownLoading}
      />
    );
  }
  if (w.widgetId === "PROFILE_VIEWS" || w.widgetId === "INMAILS_SENT") {
    return (
      <CountWidget
        value={data?.[meta.valueKey]}
        trend={data?.[meta.trendKey]}
        series={meta.seriesKey ? data?.[meta.seriesKey] : null}
        dateFilter={widgetDataStore.activeDateFilter}
        customRange={widgetDataStore.activeCustomRange}
        metricLabel={meta?.label}
        memberBreakdown={memberBreakdown}
        breakdownLoading={isBreakdownLoading}
        visualization={visualization}
      />
    );
  }
  return (
    <CountWidget
      value={data?.[meta.valueKey]}
      trend={data?.[meta.trendKey]}
      series={data?.[meta.seriesKey]}
      dateFilter={widgetDataStore.activeDateFilter}
      customRange={widgetDataStore.activeCustomRange}
      metricLabel={meta?.label}
      memberBreakdown={memberBreakdown}
      breakdownLoading={isBreakdownLoading}
      visualization={visualization}
    />
  );
};

const WidgetGrid = observer(({ widgets }) => {
  const history = useHistory();
  const sortedWidgets = [...(widgets || [])].sort((a, b) => a.position - b.position);
  const pinnedWidgets = [...sortedWidgets].filter(w => w.pinned).sort((a, b) => a.pinnedOrder - b.pinnedOrder);
  const unpinnedWidgets = sortedWidgets.filter(w => !w.pinned);
  const formatCustomSubtitle = range => {
    if (!range || range.fromDate == null || range.toDate == null) return "Custom";
    const fmt = ms => {
      const d = new Date(ms);
      const m = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"][d.getMonth()];
      return `${m} ${d.getDate()}, ${d.getFullYear()}`;
    };
    return `${fmt(range.fromDate)} – ${fmt(range.toDate)}`;
  };
  const subtitleLabel =
    widgetDataStore.activeDateFilter === "custom"
      ? formatCustomSubtitle(widgetDataStore.activeCustomRange)
      : DATE_PRESET_LABELS[widgetDataStore.activeDateFilter] || "";

  // SG-2164: Member Breakdown — persisted per-widget toggle (DashboardWidget.memberBreakdown). Turning
  // it on re-fetches that widget's data with memberBreakdown:true (backend adds a "<key>ByMember"
  // array) and saves the flag; the local Set drives the DotMenu label + whether the breakdown renders.
  const [memberBreakdownIds, setMemberBreakdownIds] = useState(new Set());

  // Restore the toggle state from the saved widgets whenever the dashboard's persisted breakdown set
  // changes (initial load / dashboard switch). The data itself is loaded breakdown-aware by
  // widgetDataStore.fetchAllWidgetData, so we only sync UI state here.
  const persistedBreakdownKey = (widgets || [])
    .filter(w => w.memberBreakdown)
    .map(w => w.widgetId)
    .sort()
    .join(",");
  useEffect(() => {
    setMemberBreakdownIds(new Set(persistedBreakdownKey ? persistedBreakdownKey.split(",") : []));
  }, [persistedBreakdownKey]);

  // SG-2164: Visualization Type switch is an async save (dashboardV2Store.updateWidgetVisualization) —
  // show the linear loader while it's in flight instead of the widget flashing with a stale chart type.
  const [switchingVisualizationIds, setSwitchingVisualizationIds] = useState(new Set());
  const handleVisualizationChange = async (widgetId, viz) => {
    setSwitchingVisualizationIds(prev => new Set(prev).add(widgetId));
    try {
      await dashboardV2Store.updateWidgetVisualization(widgetId, viz);
    } finally {
      setSwitchingVisualizationIds(prev => {
        const next = new Set(prev);
        next.delete(widgetId);
        return next;
      });
    }
  };
  // SG-2164: while a widget's "Breakdown by member" data is being fetched, show the skeleton liner
  // loader in the breakdown area (per-widget, so only the toggled widget shows it — unlike the
  // category-level isLoading which is shared across a category's widgets).
  const [breakdownLoadingIds, setBreakdownLoadingIds] = useState(new Set());
  const toggleMemberBreakdown = async widget => {
    const widgetId = widget.widgetId;
    const turningOn = !memberBreakdownIds.has(widgetId);
    setMemberBreakdownIds(prev => {
      const next = new Set(prev);
      if (next.has(widgetId)) next.delete(widgetId);
      else next.add(widgetId);
      return next;
    });
    if (turningOn) {
      setBreakdownLoadingIds(prev => new Set(prev).add(widgetId));
      try {
        await widgetDataStore.fetchWidgetMemberBreakdown(widget, dashboardV2Store.activeDashboard?.id);
      } finally {
        setBreakdownLoadingIds(prev => {
          const next = new Set(prev);
          next.delete(widgetId);
          return next;
        });
      }
    }
    // Persist the toggle so it survives reloads / dashboard switches.
    dashboardV2Store.updateWidgetMemberBreakdown(widgetId, turningOn);
  };

  const dragSection = useRef(null);
  const dragIndex = useRef(null);
  const dragOverIndex = useRef(null);
  const dragAllowed = useRef(false);
  const [dragOverId, setDragOverId] = useState(null);

  if (!sortedWidgets.length) {
    return (
      <div className="widgetGrid">
        <div className="widgetGridEmptyState">
          <p>No widgets added yet.</p>
          <button
            type="button"
            className="dashboardV2NewWidgetBtn"
            onClick={() => dashboardV2Store.setShowWidgetCatalog(true)}
          >
            <span className="dashboardV2NewWidgetBtnPlus">+</span>
            Add widget
          </button>
        </div>
      </div>
    );
  }

  const handleMouseDown = e => {
    dragAllowed.current = !!e.target.closest(".widgetCardDragHandle");
  };

  const handleDragStart = (e, section, index) => {
    if (!dragAllowed.current) {
      e.preventDefault();
      return;
    }
    dragSection.current = section;
    dragIndex.current = index;
  };

  const handleDragEnter = (index, widgetId) => {
    dragOverIndex.current = index;
    setDragOverId(widgetId);
  };

  const handleDragEnd = (section, list, reorderFn) => {
    const from = dragIndex.current;
    const to = dragOverIndex.current;
    setDragOverId(null);
    dragIndex.current = null;
    dragOverIndex.current = null;
    dragSection.current = null;
    dragAllowed.current = false;
    if (from === null || to === null || from === to) return;

    const reordered = [...list];
    const [moved] = reordered.splice(from, 1);
    reordered.splice(to, 0, moved);
    reorderFn(reordered);
  };

  const handleCrossSectionDragEnter = () => {
    if (dragSection.current !== null) {
      toasterState.setToastMsg("Pinned and unpinned widgets cannot be mixed", "fail");
    }
  };

  // SG-2164: admin gate for widget Audience Scope / Member Breakdown. Uses isTeamAdmin (member's
  // actual team role === ADMIN) rather than isTeamOwner, which is false for real team admins.
  const isAdmin = !!userDetail.userInfo.isTeamAdmin;

  const renderDraggableCard = (w, index, section, list, reorderFn) => {
    const meta = WIDGET_META[w.widgetId];
    if (!meta) return null;

    const isRate = RATE_WIDGET_IDS.has(w.widgetId);
    const isListWidget = w.widgetId === "LATEST_REPLIES";
    const loadingKey = isRate || isListWidget ? w.widgetId : w.category;
    const isLoading = widgetDataStore.isCategoryLoading(loadingKey) || widgetDataStore.getWidgetData(w.widgetId) === null;
    const data = widgetDataStore.getWidgetData(w.widgetId);

    return (
      <div
        key={w.widgetId}
        draggable
        onMouseDown={handleMouseDown}
        onDragStart={e => handleDragStart(e, section, index)}
        onDragEnter={() => handleDragEnter(index, w.widgetId)}
        onDragEnd={() => handleDragEnd(section, list, reorderFn)}
        onDragOver={e => e.preventDefault()}
        onDrop={e => e.preventDefault()}
        className={`widgetCardDragWrap${dragOverId === w.widgetId ? " widgetCardDragOver" : ""}`}
      >
        <WidgetCard
          title={meta.label}
          category={w.category}
          subtitle={subtitleLabel}
          isLoading={isLoading}
          isPinned={w.pinned}
          onPin={() => dashboardV2Store.togglePin(w.widgetId)}
          onRemove={() => dashboardV2Store.removeWidget(w.widgetId)}
          onViewRecords={
            isViewRecordsAvailable(w.widgetId)
              ? () => handleViewRecordsClick(w.widgetId, history)
              : null
          }
          isAdmin={isAdmin}
          audienceScope={w.audienceScope}
          onAudienceScopeChange={scope => dashboardV2Store.updateWidgetAudienceScope(w.widgetId, scope)}
          memberBreakdownEnabled={memberBreakdownIds.has(w.widgetId)}
          onToggleMemberBreakdown={
            isAdmin && widgetHasMemberBreakdown(w.widgetId) ? () => toggleMemberBreakdown(w) : null
          }
          visualization={w.visualization}
          defaultVisualization={getDefaultVisualization(w.widgetId)}
          onVisualizationChange={
            widgetHasGraph(w.widgetId)
              ? viz => handleVisualizationChange(w.widgetId, viz)
              : null
          }
        >
          {renderWidgetContent(
            w,
            data,
            isLoading,
            memberBreakdownIds.has(w.widgetId),
            switchingVisualizationIds.has(w.widgetId),
            breakdownLoadingIds.has(w.widgetId)
          )}
        </WidgetCard>
      </div>
    );
  };

  return (
    <div className="widgetGridWrap">
      {pinnedWidgets.length > 0 && (
        <div
          className="widgetGridSection widgetGridPinnedSection"
          onDragEnter={() => dragSection.current === "unpinned" && handleCrossSectionDragEnter()}
        >
          <div className="widgetGridSectionHeader">
            <span className="widgetGridSectionLabel">
              <PinIcon className="widgetGridSectionPinIcon" />
              Pinned
            </span>
            <span className="widgetGridSectionCount">{pinnedWidgets.length} / 3</span>
          </div>
          <div className="widgetGrid">
            {pinnedWidgets.map((w, i) =>
              renderDraggableCard(w, i, "pinned", pinnedWidgets, dashboardV2Store.reorderPinnedWidgets)
            )}
          </div>
        </div>
      )}

      {unpinnedWidgets.length > 0 && (
        <div
          className="widgetGridSection"
          onDragEnter={() => dragSection.current === "pinned" && handleCrossSectionDragEnter()}
        >
          {pinnedWidgets.length > 0 && (
            <div className="widgetGridSectionHeader">
              <span className="widgetGridSectionLabel">All widgets</span>
            </div>
          )}
          <div className="widgetGrid widgetGridUnpinned">
            {unpinnedWidgets.map((w, i) =>
              renderDraggableCard(w, i, "unpinned", unpinnedWidgets, dashboardV2Store.reorderWidgets)
            )}
          </div>
        </div>
      )}

      <button
        type="button"
        className="dashboardV2FloatingNewWidgetBtn"
        onClick={() => dashboardV2Store.setShowWidgetCatalog(true)}
      >
        <span className="dashboardV2NewWidgetBtnPlus">+</span>
        Add widget
      </button>
    </div>
  );
});

export default WidgetGrid;
